import './assets/css/App.css';
import { lazy, Suspense, useState, useLayoutEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ChakraProvider, ColorModeScript, Spinner, Flex } from '@chakra-ui/react';
import AuthLayout from './layouts/auth';
import AdminLayout from './layouts/admin';
import ScheduleStandaloneLayout from './layouts/schedule';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import FormEnterNavigation from 'components/FormEnterNavigation';
import { getStoredToken, setStoredToken } from 'api';
import initialTheme from './theme/theme';

const KioskView = lazy(() => import('./views/kiosk'));
const StaffView = lazy(() => import('./views/staff'));
const DisplayView = lazy(() => import('./views/display'));
const LandingPage = lazy(() => import('./views/landing'));

function ProtectedAdmin({ children, theme, setTheme }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  useLayoutEffect(() => {
    if (isAuthenticated) {
      const token = getStoredToken();
      if (token) {
        setStoredToken(token);
      }
    }
  }, [isAuthenticated, location.pathname]);

  if (isLoading) {
    return (
      <Flex h="100vh" align="center" justify="center">
        <Spinner size="xl" />
      </Flex>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/auth/sign-in" state={{ from: location }} replace />;
  }
  return children;
}

export default function Main() {
  const [currentTheme, setCurrentTheme] = useState(initialTheme);
  return (
    <ChakraProvider theme={currentTheme}>
      <ColorModeScript initialColorMode={initialTheme.config?.initialColorMode} />
      <FormEnterNavigation />
      <AuthProvider>
        <Routes>
          <Route path="auth/*" element={<AuthLayout />} />
          <Route
            path="admin/schedule"
            element={
              <ProtectedAdmin theme={currentTheme} setTheme={setCurrentTheme}>
                <ScheduleStandaloneLayout />
              </ProtectedAdmin>
            }
          />
          <Route
            path="admin/*"
            element={
              <ProtectedAdmin theme={currentTheme} setTheme={setCurrentTheme}>
                <AdminLayout theme={currentTheme} setTheme={setCurrentTheme} />
              </ProtectedAdmin>
            }
          />
          <Route
            path="kiosk"
            element={
              <Suspense fallback={<Flex h="100vh" align="center" justify="center"><Spinner size="xl" /></Flex>}>
                <KioskView />
              </Suspense>
            }
          />
          <Route
            path="staff"
            element={
              <Suspense fallback={<Flex h="100vh" align="center" justify="center"><Spinner size="xl" /></Flex>}>
                <StaffView />
              </Suspense>
            }
          />
          <Route
            path="display"
            element={
              <Suspense fallback={<Flex h="100vh" align="center" justify="center"><Spinner size="xl" color="white" /></Flex>}>
                <DisplayView />
              </Suspense>
            }
          />
          <Route
            path="/feedback/:type?"
            element={
              <Suspense fallback={<Flex h="100vh" align="center" justify="center"><Spinner size="xl" /></Flex>}>
                <LandingPage />
              </Suspense>
            }
          />
          <Route
            path="/"
            element={
              <Suspense fallback={<Flex h="100vh" align="center" justify="center"><Spinner size="xl" /></Flex>}>
                <LandingPage />
              </Suspense>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </ChakraProvider>
  );
}
